import { useEffect, useState } from "react"
import classNames from "classnames"

import { useLazyGetDeviceStatusesQuery } from "store/api/warehouseManagerApi"

import { WidgetLayout } from "layouts/WidgetLayout"
import { BarChart } from "components/common/BarChart"
import { Spinner } from "components/common/Spinner"

import styles from "./index.module.scss"

const initialState = {
  inStock: { v1: 0, v2: 0 },
  damaged: { v1: 0, v2: 0 },
  missing: { v1: 0, v2: 0 },
  warranty: { v1: 0, v2: 0 },
  packed: { v1: 0, v2: 0 },
}

type WidgetData = typeof initialState

interface WidgetDevicesStatusProps {
  updateIndex: number
}

export const WidgetDevicesStatus = ({ updateIndex }: WidgetDevicesStatusProps) => {
  const [triggerGetDeviceStatuses, { isFetching, data }] = useLazyGetDeviceStatusesQuery()
  const [widgetData, setWidgetData] = useState<WidgetData>(initialState)

  const sumVersion = (obj: WidgetData, version: "v1" | "v2") => {
    let sum = 0
    for (const key in obj) {
      sum += obj[key as keyof WidgetData][version]
    }
    return sum
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        await triggerGetDeviceStatuses()
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [updateIndex])

  useEffect(() => {
    if (data?.data) {
      setWidgetData({
        inStock: { v1: data.data.in_stock_v1, v2: data.data.in_stock_v2 },
        damaged: { v1: data.data.damaged_v1, v2: data.data.damaged_v2 },
        missing: { v1: data.data.missing_v1, v2: data.data.missing_v2 },
        warranty: { v1: data.data.warranty_v1, v2: data.data.warranty_v2 },
        packed: { v1: data.data.packed_v1, v2: data.data.packed_v2 },
      })
    }
  }, [data])

  const loaded = !isFetching && !!data?.data
  const v1Total = sumVersion(widgetData, "v1")
  const hasNoData = loaded && v1Total + sumVersion(widgetData, "v2") === 0

  const tagContent = (
    <span className={styles.versionTag}>
      <span>Devices</span>
      <span className={styles.widgetTagValue}>{sumVersion(widgetData, "v1")}</span>
      <span className={styles.versionNote}>(V1)</span>
      <span>/</span>
      <span className={styles.widgetTagValue}>{sumVersion(widgetData, "v2")}</span>
      <span className={styles.versionNote}>(V2)</span>
    </span>
  )

  return (
    <WidgetLayout title="Status" className={styles.devicesWrapper} tagContent={tagContent}>
      <div
        className={classNames(styles.widgetContent, {
          [styles.disabled]: isFetching,
        })}
      >
        <div className={styles.barChartContainer}>
          <BarChart
            showEmptyState={loaded}
            items={
              loaded
                ? [
                    {
                      value: widgetData.inStock.v1,
                      color: "#4ADE80",
                    },
                    {
                      value: widgetData.missing.v1,
                      color: "#FDE047",
                    },
                    {
                      value: widgetData.damaged.v1,
                      color: "#EF4444",
                    },
                    {
                      value: widgetData.warranty.v1,
                      color: "#43E0F8",
                    },
                    {
                      value: widgetData.packed.v1,
                      color: "#A855F7",
                    },
                  ]
                : [{ value: 1, color: "#E2E8F0" }]
            }
          />
        </div>
        {loaded && !hasNoData && (
          <footer className={classNames(styles.widgetFooter, styles.devicesFooter)}>
            <div className={styles.widgetFooterItem}>
              <span className={classNames(styles.valueNote, styles.green)}>In Stock</span>
              <div className={styles.widgetValueWrapper}>
                {widgetData.inStock.v1} / {widgetData.inStock.v2}
              </div>
            </div>
            <div className={styles.widgetFooterItem}>
              <span className={classNames(styles.valueNote, styles.yellow)}>Missing</span>
              <div className={styles.widgetValueWrapper}>
                {widgetData.missing.v1} / {widgetData.missing.v2}
              </div>
            </div>
            <div className={styles.widgetFooterItem}>
              <span className={classNames(styles.valueNote, styles.red)}>Damaged</span>
              <div className={styles.widgetValueWrapper}>
                {widgetData.damaged.v1} / {widgetData.damaged.v2}
              </div>
            </div>
            <div className={styles.widgetFooterItem}>
              <span className={classNames(styles.valueNote, styles.turquoise)}>Warranty</span>
              <div className={styles.widgetValueWrapper}>
                {widgetData.warranty.v1} / {widgetData.warranty.v2}
              </div>
            </div>
            <div className={styles.widgetFooterItem}>
              <span className={classNames(styles.valueNote, styles.purple)}>Packed</span>
              <div className={styles.widgetValueWrapper}>
                {widgetData.packed.v1} / {widgetData.packed.v2}
              </div>
            </div>
          </footer>
        )}
        {isFetching && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </WidgetLayout>
  )
}
